package com.ervasstudio.tracoemovimento.pages.blog

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.ervasstudio.tracoemovimento.model.NewsState
import com.ervasstudio.tracoemovimento.util.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.graphics.Colors.Black
import com.varabyte.kobweb.compose.ui.graphics.Colors.White
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.Input
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

@Composable
fun NewsLetterPopup(
    isOpen: Boolean,
    currentState: NewsState,
    externalError: String?,
    onClose: () -> Unit,
    onAcceptInvitation: () -> Unit,
    onSendEmail: (String) -> Unit
) {
    if (!isOpen) return

    // Fecha automaticamente após 3 segundos no estado SUCCESS
    LaunchedEffect(currentState) {
        if (currentState == NewsState.SUCCESS) {
            delay(3000)
            onClose()
        }
    }

    Overlay(
        modifier = Modifier
            .backgroundColor(Black.copy(alpha = 400))
            .onClick { if (currentState != NewsState.LOADING) onClose() },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .width(400.px)
                .position(Position.Relative),
            contentAlignment = Alignment.Center
        ) {
            // Reaproveitando seu fundo de popup estilizado
            Image(
                modifier = Modifier.fillMaxWidth()
                    .objectFit(ObjectFit.Fill),
                src = Res.Image.bgpopupsmall
            )

            Column(
                modifier = Modifier.fillMaxWidth().padding(24.px).onClick { it.stopPropagation() },
                horizontalAlignment = Alignment.Start
            ) {
                when (currentState) {
                    NewsState.INVITATION -> InvitationLayout(onClose, onAcceptInvitation)
                    NewsState.FORM -> NewsFormLayout(onClose, onSendEmail, externalError)
                    NewsState.LOADING -> NewsLoadingLayout()
                    NewsState.SUCCESS -> NewsSuccessLayout()
                }
            }
        }
    }
}

@Composable
fun InvitationLayout(
    onClose: () -> Unit,
    onAccept: () -> Unit
) {
    SpanText(
        "Fique por dentro!",
        Modifier.fontFamily("GothamBook")
            .fontSize(20.px)
            .fontWeight(FontWeight.Bold)
            .margin(bottom = 12.px)
            .color(Black)
    )
    SpanText(
        "Gostaria de receber novidades e actualizações sobre o projecto Traço e Movimento directamente no seu e-mail?",
        Modifier.fontFamily("GothamBook")
            .fontSize(14.px)
            .margin(bottom = 24.px)
            .color(Black)
    )

    Row(
        modifier = Modifier
            .fillMaxWidth(),
        horizontalArrangement = Arrangement.End
    ) {
        Button(
            onClick = { onClose() },
            modifier = Modifier
                .margin(right = 8.px)
        )
        {
            SpanText(
                "Não, obrigado",
                Modifier.fontFamily("GothamBook")
            )
        }
        Button(
            onClick = { onAccept() },
            modifier = Modifier
                .backgroundColor(Colors.DodgerBlue)
        ) { SpanText("Sim, claro!", Modifier.fontFamily("GothamBook").color(White)) }
    }
}

@Composable
fun NewsFormLayout(
    onCancel: () -> Unit,
    onSend: (String) -> Unit,
    externalError: String?
) {
    SpanText(
        "Subscrever notícias",
        Modifier.fontFamily("GothamBook")
            .fontSize(20.px)
            .fontWeight(FontWeight.Bold)
            .margin(bottom = 16.px)
            .color(Black)
    )

    var email by remember { mutableStateOf("") }
    var emailError by remember { mutableStateOf<String?>(null) }
    val emailRegex = "^[A-Za-z0-9+_.-]+@(.+)\$".toRegex()

    SpanText(
        "E-mail",
        modifier = Modifier
            .fontFamily("GothamBook")
            .fontSize(14.px)
            .margin(bottom = 4.px)
            .color(Black)
    )
    Input(
        type = InputType.Email, value = email,
        onValueChange = { email = it; if (emailError != null) emailError = null },
        placeholder = "seu-email@provedor.com",
        modifier = Modifier.fillMaxWidth()
            .margin(bottom = if (emailError != null || externalError != null) 2.px else 16.px)
    )

    val currentError = emailError ?: externalError
    currentError?.let {
        SpanText(
            it,
            Modifier.color(Colors.Red).fontSize(12.px).margin(bottom = 16.px)
                .fontFamily("GothamBook")
        )
    }

    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
        Button(onClick = { onCancel() }, modifier = Modifier.margin(right = 8.px)) {
            SpanText(
                "Cancelar",
                Modifier.fontFamily("GothamBook")
            )
        }
        Button(
            onClick = {
                if (!email.matches(emailRegex)) {
                    emailError = "Formato de e-mail inválido."
                } else {
                    onSend(email.trim())
                }
            },
            modifier = Modifier.backgroundColor(Colors.DodgerBlue)
        ) { SpanText("Subscrever", Modifier.fontFamily("GothamBook").color(White)) }
    }
}

@Composable
fun NewsLoadingLayout() {
    Column(
        modifier = Modifier.fillMaxWidth().padding(topBottom = 20.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            "A processar a inscrição...",
            Modifier
                .fontFamily("GothamBook")
                .fontSize(16.px)
                .color(Black)
        )
    }
}

@Composable
fun NewsSuccessLayout() {
    Column(
        modifier = Modifier.fillMaxWidth().padding(topBottom = 20.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            "Inscrição confirmada!",
            Modifier.fontFamily("GothamBook").fontSize(18.px).fontWeight(FontWeight.Bold)
                .color(Colors.Green).margin(bottom = 4.px)
        )
        SpanText("Obrigado por nos acompanhar.", Modifier.fontFamily("GothamBook").fontSize(14.px).color(White))
    }
}